import { useTranslation } from 'react-i18next';
import { TriangleAlert } from 'lucide-react';

import { Badge } from '@/components/ui/badge';

import { rendersEmpty } from './types';
import type { IVariableItem, VariableIconName } from './types';
import { VariableItemIcon } from './variable-icons';

interface VariableRowProps {
    readonly item: IVariableItem;
    /**
     * The category this row sits under. Only its TINT is used — the category's
     * name belongs to the group heading above, not repeated on every row.
     */
    readonly groupIcon?: VariableIconName;
}

/**
 * One variable row, written for a person: an icon, the human label, and nothing
 * else unless there is something useful to add.
 *
 * The raw `{{token}}` is deliberately NOT rendered. It is an implementation
 * detail of how the value is stored, and printing it on every row is what made
 * the picker read as code. It stays reachable as the row's native tooltip for
 * anyone who wants to know what actually gets inserted.
 */
export function VariableRow({ item, groupIcon }: VariableRowProps) {
    const { t } = useTranslation();
    const isEmptyRender = rendersEmpty(item);
    const secondary = [item.description, item.example].filter(Boolean).join(' · ');

    return (
        <div className="flex min-w-0 flex-1 items-start gap-2" title={`{{${item.token}}}`}>
            {/* `mt-0.5` optically centres the glyph on the label's cap height
                rather than on the whole two-line block. */}
            <VariableItemIcon
                item={item}
                groupIcon={groupIcon}
                className="mt-0.5 size-4 shrink-0"
            />
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <div className="flex min-w-0 items-center gap-1.5">
                    <span className="text-text-primary truncate text-sm">{item.label}</span>
                    {/* Only where the type is a WARNING, not everywhere it is
                        known: the icon already says "number" or "date", but
                        nothing about a glyph says "and this renders as nothing
                        in a message body". */}
                    {isEmptyRender && item.type && (
                        <Badge variant="default" className="shrink-0 font-normal">
                            {item.type}
                        </Badge>
                    )}
                    {isEmptyRender && (
                        <TriangleAlert
                            className="text-warning-600 size-3.5 shrink-0"
                            aria-label={t(
                                'variables.emptyRenderWarning',
                                'This is a structured value — it renders as nothing in a message body.',
                            )}
                        />
                    )}
                </div>

                {secondary && (
                    <span className="text-text-secondary truncate text-xs">{secondary}</span>
                )}
            </div>
        </div>
    );
}
